import { useMemo } from "react";
import { format } from "date-fns";
import { getInterval, getPercent } from "@helpers";
import { Investment, useShowHistoricQuote } from "@rentablo/api/apiv1";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import { HistoricQuote } from "@api";

export const prepareChartData = (
  quotes: HistoricQuote[],
  comparedQuotes: HistoricQuote[]
): {
  totalValues: [number, number][];
  comparisonValues: [number, number][];
  investedCapitalValues: [number, number][];
} => {
  if (!quotes || quotes.length === 0) {
    return { totalValues: [], comparisonValues: [], investedCapitalValues: [] };
  }

  let reference = quotes[0];
  let referenceCompared: HistoricQuote | undefined = comparedQuotes?.[0];
  let value = 1;
  let comparedValue = 1;

  const totalValues: [number, number][] = [];
  const comparisonValues: [number, number][] = [];

  quotes.forEach((current) => {
    value *= current.value / reference.value;

    const compareQuote = comparedQuotes.find(
      (quote) => quote.date === current.date
    );

    if (compareQuote) {
      if (!referenceCompared) referenceCompared = compareQuote;
      comparedValue *= compareQuote.value / referenceCompared.value;
      referenceCompared = compareQuote;
    }

    reference = current;

    const percents = getPercent(value);
    const secondaryPercents = getPercent(comparedValue);

    totalValues.push([new Date(current.date).getTime(), percents]);
    comparisonValues.push([
      new Date(current.date).getTime(),
      secondaryPercents,
    ]);
  });

  return { totalValues, comparisonValues, investedCapitalValues: [] };
};

export const useGetHistoricQuotes = (
  range: {
    startDate: Date;
    endDate: Date;
  },
  recommendation: FundRecommendationResponse,
  investment?: Investment
) => {
  const investmentsQuoteEnabled = Boolean(
    investment?.tickerSymbol && investment?.quote?.quoteProvider
  );
  const {
    data: originalQuote,
    isFetched: isOriginalQuoteFetched,
    isFetching: isOriginalQuoteFetching,
  } = useShowHistoricQuote(
    {
      tickerSymbol: investment?.tickerSymbol!,
      quoteProvider: investment?.quote?.quoteProvider!,
      from: format(range.startDate, "yyyy-MM-dd"),
      to: format(range.endDate, "yyyy-MM-dd"),
      //@ts-ignore backend to add DAY
      interval: getInterval(range.startDate, range.endDate),
    },
    {
      enabled: Boolean(
        investment?.tickerSymbol && investment?.quote?.quoteProvider
      ),
    }
  );

  const {
    data: recommendationQuote,
    isFetched: isRecommendationQuoteFetched,
    isFetching: isRecommendationQuoteFetching,
  } = useShowHistoricQuote(
    {
      tickerSymbol: recommendation.fund.isin!,
      quoteProvider: "ariva",
      from: format(range.startDate, "yyyy-MM-dd"),
      to: format(range.endDate, "yyyy-MM-dd"),
      //@ts-ignore backend to add DAY
      interval: getInterval(range.startDate, range.endDate),
    },
    {
      enabled: Boolean(recommendation.fund.isin),
    }
  );

  const isFetched =
    (investmentsQuoteEnabled ? isOriginalQuoteFetched : true) &&
    isRecommendationQuoteFetched;
  const isFetching = isOriginalQuoteFetching || isRecommendationQuoteFetching;

  const chartData = useMemo(() => {
    if (isFetching)
      return {
        totalValues: [],
        comparisonValues: [],
        investedCapitalValues: [],
      };

    return prepareChartData(
      (originalQuote?.historicQuotes as HistoricQuote[]) ?? [],
      (recommendationQuote?.historicQuotes as HistoricQuote[]) ?? []
    );
  }, [originalQuote, recommendationQuote, isFetching]);

  return {
    chartData,
    isFetching,
    isFetched,
  };
};
